import React from 'react';
import classNames from 'classnames';
import classes from '@nerdwallet/base-styles/classes';
import Image from '@nerdwallet/react-image';
import {
  Box,
  CenteredContainer,
  Title,
  Text,
  Link,
} from '@nerdwallet/currency';
import styles from './socialimpact-page.module.less';
import BoxTable from '~/client/components/employee-branding/box-table/box-table';
import BoxTableNarrowData from '~/client/lib/employee-branding/box-table-narrow-data';
import SocialImpactCarousel from '../../employee-branding/socialimpact-carousel/socialimpact-carousel';
import carouselData from '../../../lib/company-content-src/socialimpact-carousel-content';
import BoxTableData from '~/client/lib/employee-branding/box-table-data';
import ImpactPyramid from '../impact-pyramid/impact-pyramid';

const inclusiveHero = {
  image:
    'https://www.nerdwallet.com/cdn/landing-pages/CSR/father-son-collage.png',
  backgroundColor: '#EFFCF8',
  desktopLogo:
    'https://www.nerdwallet.com/cdn/landing-pages/CSR/nw-inclusiv-lockup_desktop.png',
  mobileLogo:
    'https://www.nerdwallet.com/cdn/landing-pages/CSR/nw-inclusiv-lockup_mobile.png',
};

const HowYouAndInclusivCanHelp = [
  {
    number: 1,
    text: 'Inclusiv partners with you to identify credit unions in need of capital',
  },
  { number: 2, text: 'Your company deposits your investment with CDCUs' },
  {
    number: 3,
    text: 'CDCUs invest back into the community with safe financial products and loans',
  },
  {
    number: 4,
    text: 'Your company earns interest while community members build financial security',
  },
];

const SocialImpactPage = () => (
  <Box>
    {/* Page Hero */}
    <Box style={{ backgroundColor: '#f4eee6' }}>
      <Box className={styles.hero}>
        <Box className={styles.heroTextContainer}>
          <Title size="default" className={styles.heroTitle}>
            The Financial Equity Pledge
          </Title>
          <Text>
            NerdWallet envisions a world where everyone makes financial
            decisions with confidence. Regardless of race, ethnicity, or income,
            we believe everyone deserves the tools needed to achieve financial
            health and well-being.
          </Text>
        </Box>
        <Box>
          <Image
            backgroundColor="transparent"
            naturalHeight={425}
            naturalWidth={425}
            className={styles.heroImage}
            src="https://www.nerdwallet.com/cdn/landing-pages/CSR/hero-collage_desktop.png"
          />
        </Box>
      </Box>
    </Box>
    {/* Sub Hero */}
    <div className={styles.subHero}>
      <Title className={classNames([classes('margin-bottom-3')])} size="small">
        But today, there are barriers preventing many people from achieving
        greater financial equity.
      </Title>
      <Text>
        Approximately <strong>63 million adults</strong> in the United States
        are <strong>unbanked or underbanked</strong>, with Black and Hispanic
        individuals comprising nearly 60% of this population.*
      </Text>
      <Box className={classNames([classes('margin-top-4')])}>
        <Text>
          <strong>We&#39;re committed to changing that.</strong>
        </Text>
      </Box>
    </div>
    <Box className={styles.floatImage}>
      <Image
        backgroundColor="transparent"
        naturalHeight={154}
        naturalWidth={154}
        src="https://www.nerdwallet.com/cdn/landing-pages/CSR/Squiggle_arrow.gif"
      />
    </Box>
    {/* Inclusive Hero */}
    <Box
      className={classNames([
        styles.inclusiveHeroContainer,
        classes('background-green-lightest'),
      ])}
    >
      <Box className={styles.hero}>
        <Box className={styles.heroTextContainer}>
          <Image
            backgroundColor="transparent"
            naturalHeight={42}
            naturalWidth={550}
            className={styles.heroLogoDesktop}
            src={inclusiveHero.desktopLogo}
          />
          <Image
            backgroundColor="transparent"
            naturalHeight={127}
            naturalWidth={375}
            className={styles.heroLogoMobile}
            src={inclusiveHero.mobileLogo}
          />
          <Text>
            NerdWallet has joined forces with Inclusiv, a national nonprofit on
            a mission to help low to moderate-income communities achieve
            financial independence through community development credit unions
            (CDCUs).
            <br />
            <br />
            CDCUs provide these communities with{' '}
            <strong>increased access</strong> to safe and secure financial
            products and services, like fair loans, savings accounts, and
            financial counseling.
            <br />
            <br />
            Together with Inclusiv, we&#39;re championing a movement to close
            the financial gap in underserved communities and build a more
            equitable financial future for all.
          </Text>
        </Box>
        <Box>
          <Image
            backgroundColor="transparent"
            naturalHeight={425}
            naturalWidth={425}
            className={styles.heroImage}
            src={inclusiveHero.image}
          />
        </Box>
      </Box>
    </Box>
    {/* Parallax */}
    <div
      className={styles.parallax}
      style={{
        backgroundImage:
          'url("https://www.nerdwallet.com/cdn/landing-pages/CSR/23Q2_MKTG_CSR-3.0_parallax_medium.jpg")',
      }}
    >
      <Box className={styles.parallaxTextContainer}>
        <Title size="small" className={styles.parallaxText}>
          But we can&#39;t do it alone. Join The Financial Equity Pledge today.
        </Title>
      </Box>
    </div>
    <Box>
      <Title
        className={classNames([
          classes(
            'margin-horizontal-0',
            'text-align-center',
            'margin-top-5',
            'margin-bottom-4'
          ),
        ])}
        size="small"
      >
        How to get involved
      </Title>
      <SocialImpactCarousel {...carouselData} />
    </Box>
    {/* Partners */}
    <Box
      className={classNames([
        classes('background-blue-lightest'),
        classes('padding-all-5'),
      ])}
    >
      <Box
        style={{ maxWidth: '760px' }}
        className={classNames([classes('margin-horizontal-auto')])}
      >
        <Title
          className={classNames([
            classes('text-align-center', 'margin-bottom-3'),
          ])}
          size="small"
        >
          Partners who&#39;ve joined the cause
        </Title>
        <Box className={classNames([classes('text-align-center')])}>
          <Text>
            We may have started the movement, but we&#39;re not alone. You and
            your organization can join these partners who are committed to
            making a difference in financial equality.
          </Text>
        </Box>
        <Box className={styles.partnersSectionImageContainer}>
          <Image
            backgroundColor="transparent"
            naturalHeight={103}
            naturalWidth={367}
            objectFit="contain"
            className={styles.partnersSectionImage}
            src="https://www.nerdwallet.com/cdn/landing-pages/CSR/hartimpact_and_matchgroup.png"
          />
        </Box>
      </Box>
    </Box>
    <Box
      className={classNames([
        classes(
          'display-flex',
          'justify-content-center',
          'align-items-center',
          'margin-all-5--wide'
        ),
      ])}
    >
      <div
        className={classNames([
          classes('display-none'),
          classes('display-block--wide'),
        ])}
      >
        <Image
          style={{ maxWidth: 540 }}
          backgroundColor="transparent"
          naturalHeight={540}
          naturalWidth={540}
          objectFit="contain"
          src="https://www.nerdwallet.com/cdn/landing-pages/CSR/collage-diagram_desktop.png"
        />
      </div>
      <Box
        styles={{ maxWidth: '650px' }}
        className={classNames([classes('margin-left-5--wide', 'margin-all-3')])}
      >
        <Title
          className={classNames([
            classes('display-none--wide', 'text-align-center', 'margin-top-3'),
          ])}
          size="small"
        >
          Our partnership with Inclusiv
        </Title>
        <div
          className={classNames([
            classes(
              'display-none--wide',
              'display-flex',
              'justify-content-center',
              'margin-vertical-4'
            ),
          ])}
        >
          <Image
            style={{ maxHeight: 330, width: 'auto' }}
            backgroundColor="transparent"
            naturalHeight={330}
            naturalWidth={330}
            src="https://www.nerdwallet.com/cdn/landing-pages/CSR/collage-diagram_desktop.png"
          />
        </div>
        <Title
          className={classNames([
            classes('text-align-center', 'text-align-left--wide'),
          ])}
          size="small"
        >
          How you can help:
        </Title>
        {HowYouAndInclusivCanHelp.map((item) => (
          <Box
            key={item.number}
            className={classNames([
              classes(
                'display-flex',
                'align-items-center',
                'margin-vertical-3',
                'margin-horizontal-3-half'
              ),
            ])}
          >
            <Title
              className={classNames([classes('margin-right-4')])}
              size="small"
            >
              {item.number}
            </Title>
            <Text>{item.text}</Text>
          </Box>
        ))}
      </Box>
    </Box>
    <Box
      className={classNames([
        classes('padding-bottom-4--wide'),
        classes('background-green-lightest'),
      ])}
    >
      <CenteredContainer
        className={classNames([
          classes(
            'padding-vertical-5',
            'padding-bottom-5',
            'text-align-center',
            'text-align-left--wide'
          ),
        ])}
      >
        <Title
          className={classNames([
            classes(
              'text-align-center',
              'margin-bottom-5--wide',
              'margin-bottom-4'
            ),
          ])}
          size="small"
        >
          Hear from CDCU Members
        </Title>
        <BoxTable {...BoxTableData} />
      </CenteredContainer>
    </Box>
    {/* ImpactPyramid */}
    <ImpactPyramid />
    {/* Looking to learn */}
    <Box className={styles.lookingToLearnSection}>
      <CenteredContainer
        className={classNames([
          classes('margin-bottom-3-half'),
          classes('margin-bottom-5--wide'),
        ])}
      >
        <Title
          size="small"
          align="center"
          className={classNames([
            classes('margin-horizontal-auto'),
            classes('text-align-left'),
            classes('margin-bottom-4'),
            classes('text-align-center--wide'),
          ])}
          component="h2"
        >
          Looking to learn more about this issue?
        </Title>
        <BoxTable {...BoxTableNarrowData} />
      </CenteredContainer>
    </Box>
    <Box className={styles.learnMoreSection}>
      <Box className={styles.learnMoreSectionImage}>
        <Image
          backgroundColor="transparent"
          src="https://www.nerdwallet.com/cdn/landing-pages/CSR/tim-portrait.png"
          naturalHeight={200}
          naturalWidth={200}
        />
      </Box>
      <Box className={styles.learnMoreSectionTextContainer}>
        <Title className={styles.learnMoreSectionTitle} size="small">
          Why NerdWallet Invests in CDCUs
        </Title>
        <div className={styles.learnMoreTextDesktop}>
          <Text component="p" align="left">
            Read about NerdWallet CEO Tim Chen&#39;s commitment to investing in
            CDCUs in{' '}
            <Link href="https://www.fastcompany.com/90651390/most-creative-people-2021-tim-chen">
              Fast Company
            </Link>{' '}
            and{' '}
            <Link href="https://www.triplepundit.com/story/2022/companies-unbanked-americans/746621">
              Triple Pundit.
            </Link>
          </Text>
        </div>
        <div className={styles.learnMoreTextMobile}>
          <Text component="p" align="center">
            Read about NerdWallet CEO Tim Chen&#39;s commitment to investing in
            CDCUs in{' '}
            <Link href="https://www.fastcompany.com/90651390/most-creative-people-2021-tim-chen">
              Fast Company
            </Link>{' '}
            and{' '}
            <Link href="https://www.triplepundit.com/story/2022/companies-unbanked-americans/746621">
              Triple Pundit.
            </Link>
          </Text>
        </div>
      </Box>
    </Box>
    <Box
      className={classNames([
        classes('margin-top-5'),
        classes('margin-bottom-4'),
        classes('padding-horizontal-5--wide'),
        classes('padding-horizontal-3'),
      ])}
    >
      <Box
        borderRadius="2"
        style={{ maxWidth: 1152 }}
        className={classNames([
          classes('background-green-lightest'),
          classes('padding-vertical-5'),
          classes('text-align-center'),
          classes('margin-horizontal-auto'),
        ])}
      >
        <Title size="small" className={styles.fontFamilyGotham}>
          Now is the time to help
        </Title>
        <Box
          style={{ maxWidth: 660 }}
          className={classNames([
            classes('margin-horizontal-auto'),
            classes('margin-vertical-3'),
            classes('padding-horizontal-3'),
          ])}
        >
          <Text>
            Want to help by depositing some of your company’s banked dollars?
            Email us at{' '}
            <Link href="mailto:socialimpact@nerdwallet.com">
              socialimpact@nerdwallet.com
            </Link>
            .
          </Text>
        </Box>
      </Box>
      <Box className={styles.footNoteContainer}>
        <Text size="small">
          *Source: 2021 FDIC National Survey of Unbanked and Underbanked
          Households
        </Text>
      </Box>
    </Box>
  </Box>
);

SocialImpactPage.displayName = 'Social Impact';

export default SocialImpactPage;
